import Button from "components/ui/Button";
import TextInput from "components/ui/TextInput";
import PasswordRequirement from "components/PasswordRequirement";
import Spinner from "components/ui/Spinner";
import { useLocalization } from "contexts/LocalizationContext";
import { UserContext } from "contexts/UserContext";
import { useRequiredContext } from "contexts/utils";
import { routes } from "pages/routes";
import { ChangeEvent, FocusEvent, type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { signUp, type SignUpParams } from "services/users/auth";
import { toErrorText } from "services/errors";
import PasswordInput from "components/ui/PasswordInput";
import useAccountForm from "../../../SignIn/utils";

import assert from "utils/assert";
import styles from "./EmailSignUp.module.scss";
import { trackEvent } from "services/productAnalytics";
import { PurchaseStudentType } from "services/pricingModel/model";
import ErrorBanner from "components/ui/ErrorBanner";

type Props = {
  studentType: PurchaseStudentType;
  pricingModelCode?: string;
  onSignUp: (email: string, resend_verification_token: string) => void;
};

const initData = {
  firstName: "",
  email: "",
  password: "",
  confirmPassword: "",
};

export default function EmailSignUp({ pricingModelCode, onSignUp, studentType }: Props) {
  const {
    account,
    setAccount,
    errors,
    touched,
    passwordStatus,
    setTouched,
    onValidate,
    onTouched,
  } = useAccountForm({
    ...initData,
    ...(pricingModelCode && {
      lastName: "",
    }),
  });
  const [loading, setLoading] = useState(false);
  const [createErrors, setCreateErrors] = useState<unknown>(null);
  const { t } = useTranslation();

  const { language } = useLocalization();
  const { refreshCurrentUser } = useRequiredContext(UserContext);

  const isCreateDisabled = Object.keys(errors).length > 0;

  const handleChangeAccountData = (e: ChangeEvent<HTMLInputElement>) => {
    setAccount({ ...account, [e.target.name]: e.target.value });
  };

  const handleCreate = async (evt: FormEvent<HTMLFormElement>) => {
    evt.preventDefault();

    const isValidated = onValidate();
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      countryCode: true,
      primaryLanguage: true,
      password: true,
      confirmPassword: true,
      location: true,
    });

    if (isValidated) {
      setLoading(true);

      try {
        const { email, password, firstName } = account;
        assert(email && password && firstName && language, "Missing required fields");
        const address = account.location?.address;
        const profile =
          address?.state !== undefined || address?.street_1 !== undefined
            ? {
                state: address.state,
                street_1: address.street_1,
              }
            : undefined;

        const payload: SignUpParams = {
          email,
          passwd: password,
          first_name: firstName,
          last_name: account.lastName,
          city: address?.city,
          country: address?.country,
          timezone: account.location?.timezoneId,
          native_language: account.primaryLanguage ?? undefined,
          profile,
          localization_lang: language,
          student_type: studentType,
          pricing_model_code: pricingModelCode,
        };

        trackEvent("email_sign_up_submitted", { pricingModelCode });

        const resp = await signUp(payload);
        if (resp.user.status === "active") {
          await refreshCurrentUser();
        }

        if (resp.sso?.redirect_url) {
          window.location.replace(resp.sso.redirect_url);
        } else if (account.email) {
          onSignUp(account.email, resp.resend_verification_token);
          setLoading(false);
          trackEvent("email_sign_up_success");
        }
      } catch (error) {
        setLoading(false);
        setCreateErrors(error);
        trackEvent("sign_up_error", { error });
      }
    }
  };

  const handleBlurField = (e: FocusEvent<HTMLInputElement>) => {
    onTouched(e.target.name);
  };

  const getCreateErrorComponents = (error: unknown) => {
    let errorString = toErrorText(error);
    const isLoginLink = errorString.includes("Log in here");

    if (isLoginLink) {
      errorString = errorString.replace("Log in here.", "");
    }

    return (
      <ErrorBanner>
        {errorString}
        {isLoginLink && (
          <>
            <br />
            <Link to={routes.public.signIn.path()}>
              {t("Auth.emailSignup.logInHere")}
            </Link>
          </>
        )}
      </ErrorBanner>
    );
  };

  return (
    <>
      <h2 className={styles.title}>{t("Auth.CreateAccount.emailSignup.headline")}</h2>

      <form onSubmit={evt => void handleCreate(evt)} className={styles.form}>
        {createErrors ? getCreateErrorComponents(createErrors) : null}

        <div className={styles.formWrapper}>
          <TextInput
            label={t("Auth.emailSignup.firstNameLabel")}
            error={touched.firstName && errors.firstName}
            size="extra-large"
            className={styles.textField}
            name="firstName"
            value={account.firstName}
            onChange={handleChangeAccountData}
            onBlur={handleBlurField}
            invalid={!!(errors.firstName && touched.firstName)}
          />
          {pricingModelCode && (
            <TextInput
              label={t("Auth.emailSignup.lastNameLabel")}
              error={touched.lastName && errors.lastName}
              size="extra-large"
              className={styles.textField}
              name="lastName"
              value={account.lastName}
              onChange={handleChangeAccountData}
              onBlur={handleBlurField}
              invalid={!!(errors.lastName && touched.lastName)}
            />
          )}
          <TextInput
            label={t("Auth.emailSignup.emailLabel")}
            error={touched.email && errors.email}
            size="extra-large"
            className={styles.textField}
            name="email"
            value={account.email}
            onChange={handleChangeAccountData}
            onBlur={handleBlurField}
            invalid={!!(errors.email && touched.email)}
          />
          <PasswordInput
            label={t("Auth.emailSignup.passwordLabel")}
            error={touched.password && errors.password}
            size="extra-large"
            className={styles.textField}
            name="password"
            autoComplete="new-password"
            value={account.password}
            invalid={!!(errors.password && touched.password)}
            onChange={handleChangeAccountData}
            onBlur={handleBlurField}
          />
          <PasswordInput
            label={t("Auth.emailSignup.confirmPasswordLabel")}
            error={touched.confirmPassword && errors.confirmPassword}
            size="extra-large"
            className={styles.textField}
            name="confirmPassword"
            autoComplete="new-password"
            value={account.confirmPassword}
            invalid={!!(errors.confirmPassword && touched.confirmPassword)}
            onChange={handleChangeAccountData}
            onBlur={handleBlurField}
          />
        </div>

        {account.password && (
          <div className={styles.passwordConfirmPanel}>
            <PasswordRequirement passwordStatus={passwordStatus} />
          </div>
        )}

        <div className={styles.stepAction}>
          {!loading ? (
            <Button
              className={styles.btn}
              size="large"
              type="submit"
              disabled={isCreateDisabled}
            >
              {t("Auth.emailSignup.submit")}
            </Button>
          ) : (
            <Spinner />
          )}
        </div>
      </form>
    </>
  );
}
